// 문서함 유틸 — 바닐라 app.js 의 libViewUrl/libViewInTab/libDownload/libToast/
// buildLibraryIndex 이식 (Phase 3-B). 파일 저장소는 따로 두지 않는다:
// 문서의 진실 소스는 대화 스냅샷(messagesHtml) 안의 다운로드 아티팩트다.
import { labFiles } from "./labFiles";
import type { VaSession } from "../stores/chat";

export interface LibItem {
  url: string;
  label: string;
  kind: string;
  ext: string;
  company: string;
  created: string;
  feature: string;
  featureKey: string;
  title: string;
  preview: string[];
  convId: string;
  convTitle: string;
  tsMs: number;
}

const LIB_FEATURES: Record<string, string> = {
  investor: "투자자 보고서", credit: "기업 리포트", research: "기업조사 보고서",
  financials: "재무 요약", screener: "기업 스크리닝",
};

/** 파일 종류 판별 — doc 메타(kind) → URL 확장자 → 라벨 키워드 → 라벨 확장자 순 폴백.
 *  미리보기·문서함·아티팩트 버튼이 공유하는 단일 판별기 (중복 구현 금지). */
export function libKindOf(
  fname: string,
  doc: Record<string, unknown> | null,
  url = "",
): string {
  if (doc && (doc.kind === "report" || doc.kind === "excel" || doc.kind === "ppt")) return doc.kind as string;
  const path = url.split("?")[0].toLowerCase();
  if (path.endsWith(".pdf")) return "report";
  if (/\.(xlsx|xls|csv)$/.test(path)) return "excel";
  if (/\.(pptx|ppt)$/.test(path)) return "ppt";
  // 라벨 키워드 폴백 — 'Excel 다운로드'처럼 확장자 없는 라벨
  if (/엑셀|excel|xlsx/i.test(fname)) return "excel";
  if (/ppt|프레젠테이션/i.test(fname)) return "ppt";
  if (/pdf|보고서/i.test(fname)) return "report";
  const f = (fname || "").toLowerCase();
  if (f.endsWith(".pdf")) return "report";
  if (/\.(xlsx|xls|csv)$/.test(f)) return "excel";
  return "file";
}

/** 대화 스냅샷을 스캔해 생성 문서(보고서·엑셀) 인덱스를 만든다.
 *  doc 메타가 없는 예전 아티팩트는 파일명에서 최대한 복원한다(기업명_종류_날짜 관례). */
export function buildLibraryIndex(sessions: VaSession[]): LibItem[] {
  const seen = new Set<string>();
  const items: LibItem[] = [];
  // lab 페이지(버핏·유사기업) 산출물 — 대화 밖에서 생성되므로 기록소에서 병합.
  // convId 에 페이지 경로를 넣어 '원본으로 이동'이 해당 페이지를 연다.
  for (const f of labFiles()) {
    const furl = normalizeDocUrl(f.url);
    if (!furl || seen.has(furl)) continue;
    seen.add(furl);
    items.push({
      url: furl, label: f.label, kind: f.kind,
      ext: (f.label.match(/\.([a-z0-9]+)$/i) || [])[1]?.toLowerCase() || (f.kind === "excel" ? "xlsx" : "pdf"),
      company: f.company, created: f.created.slice(0, 10),
      feature: f.feature, featureKey: "lab",
      title: f.company ? `${f.company} ${f.feature} 보고서` : `${f.feature} 보고서`,
      preview: [f.label],
      convId: f.page, convTitle: `${f.feature} 페이지`,
      tsMs: Date.parse(f.created) || 0,
    });
  }
  for (const s of sessions) {
    if (!s || !s.messagesHtml) continue;
    let dom: Document;
    try {
      dom = new DOMParser().parseFromString(s.messagesHtml, "text/html");
    } catch {
      continue;
    }
    dom.querySelectorAll(".art-download a[href]").forEach((aEl) => {
      // 예전 스냅샷의 href 는 encodeURI 로 이중 인코딩돼 있을 수 있다 — 정규화해 같은 문서를 하나로
      const url = normalizeDocUrl(aEl.getAttribute("href") || "");
      if (!url || seen.has(url)) return;
      seen.add(url);
      const label = (aEl.textContent || "").trim() || "문서";
      let doc: Record<string, unknown> | null = null;
      try {
        doc = JSON.parse(aEl.getAttribute("data-doc") || "null");
      } catch {
        /* 예전 아티팩트 — 메타 없음 */
      }
      const kind = libKindOf(label, doc, url);
      // 날짜 = 문서 '생성일' — doc.created(생성 시점 기록) → 파일명 날짜 → 그 대화의 시점 순
      const created =
        (doc && (doc.created as string)) ||
        (label.match(/\d{4}-\d{2}-\d{2}/) || [])[0] ||
        (s.tsMs ? new Date(Number(s.tsMs)).toISOString().slice(0, 10) : "");
      const company =
        (doc && (doc.company as string)) || (label.includes("_") ? label.split("_")[0] : "");
      const featureKey = (doc && (doc.feature as string)) || "";
      const feature =
        LIB_FEATURES[featureKey] ||
        (kind === "report" ? "보고서" : kind === "excel" ? "엑셀" : "파일");
      // 카드 제목 — 파일명이 아니라 의미 있는 이름 (기업+종류 / 스크리닝 조건).
      // 'Excel 다운로드' 같은 제네릭 라벨은 대화 제목으로 대체한다 (DCF 산출물 등)
      const generic = /^(다운로드|Excel 다운로드|PPT 다운로드|PDF 다운로드)$/.test(label);
      const title =
        featureKey === "screener" && doc && doc.title
          ? `기업 스크리닝 — ${doc.title}`
          : doc && doc.title
            ? (doc.title as string)
            : generic
              ? s.title && s.title !== "새 채팅"
                ? `${s.title} 산출 파일`
                : "산출 파일"
              : company
                ? `${company} ${feature}`
                : label.replace(/\.(pdf|xlsx|xls|csv)$/i, "");
      // 프리뷰(실제 내용 일부): doc.preview(서버 생성) → 그 답변 본문 발췌 → 종류 라벨
      let preview = (doc && (doc.preview as string[] | string)) || null;
      if (typeof preview === "string") preview = [preview];
      if (!preview || !preview.length) {
        const art = aEl.closest("article");
        const snip = art
          ? String(art.querySelector(".message-text")?.textContent || "")
              .replace(/\s+/g, " ")
              .trim()
              .slice(0, 150)
          : "";
        preview = snip ? [feature, snip] : [feature];
      }
      // 확장자 — 파일명에 없으면 URL 경로에서 (DCF 엑셀 등 라벨이 '다운로드'인 산출물)
      const ext =
        (
          (label.match(/\.(\w+)$/) || [])[1] ||
          (url.split("?")[0].match(/\.(\w{2,5})$/) || [])[1] ||
          (kind === "excel" ? "xlsx" : kind === "report" ? "pdf" : kind === "ppt" ? "pptx" : "")
        ).toUpperCase() || "기타";
      items.push({
        url, label, kind, ext, company, created, feature, featureKey, title,
        preview: preview as string[],
        convId: s.id, convTitle: s.title || "", tsMs: Number(s.tsMs) || 0,
      });
    });
  }
  items.sort(
    (a, b) => (b.created || "").localeCompare(a.created || "") || b.tsMs - a.tsMs,
  );
  return items;
}

// 새 탭 보기 — 서버가 파일을 attachment 로 내리면(토큰 경로 기본, S3 도 흔함) 브라우저가
// 무조건 다운로드해 버려서, 보기 경로는 서버가 inline disposition 을 강제하는 URL 로 연다.
// 클릭 핸들러에서 동기 window.open — await 뒤에 열면 팝업 차단에 걸린다.
/** 엑셀·CSV 는 브라우저 뷰어가 없어 그리드 뷰어 페이지(/agent/doc-view)를 쓴다.
 *  same-origin iframe 이라 로그인 쿠키·클립보드·테마가 그대로 통한다.
 *  URL 은 상대경로(/agent/report/<token>)를 그대로 넘긴다 — 토큰 파일은 서버가
 *  디스크에서 직접 읽으므로, origin 절대화하면 오히려 호스트 화이트리스트에
 *  걸려 로컬 개발에서 400 이 된다. */
export function libExcelViewUrl(url: string): string {
  return "/agent/doc-view?url=" + encodeURIComponent(url);
}

/** 문서 URL 정규화 — 이미 %XX 로 인코딩된 서명 URL 을 encodeURI 로 한 번 더 감싸면 %25XX 가 되어
 *  서명 검증(403)에 실패한다(S3 서명 링크 전환 뒤 실제로 났다). 이중 인코딩은 한 겹 벗기고,
 *  한글·공백이 날것으로 있으면 한 번만 인코딩한다(new URL 은 기존 %XX 를 건드리지 않는다). */
export function normalizeDocUrl(url: string): string {
  let u = String(url || "").trim();
  if (!u) return u;
  for (let i = 0; i < 3 && /%25[0-9A-Fa-f]{2}/.test(u); i += 1) u = u.replace(/%25([0-9A-Fa-f]{2})/g, "%$1");
  if (/^https?:\/\//i.test(u)) {
    try { return new URL(u).href; } catch { return encodeURI(u); }
  }
  return /%[0-9A-Fa-f]{2}/.test(u) ? u : encodeURI(u);
}

export function libViewUrl(rawUrl: string): string {
  const url = normalizeDocUrl(rawUrl);
  if (url.startsWith("/agent/report/")) return url + (url.includes("?") ? "&" : "?") + "inline=1";
  if (/^https:\/\/[^/]+\.(amazonaws|deepsearch)\.com\//.test(url)) {
    return "/agent/report-view?url=" + encodeURIComponent(url);
  }
  return url;
}

export function libViewInTab(url: string) {
  window.open(libViewUrl(url), "_blank", "noopener");
}

/** 우측 미리보기 패널 iframe 경로 — PDF(report)는 inline 보기, 엑셀/CSV(excel)는
 *  서버 HTML 렌더. 그 외 종류(ppt·기타)는 브라우저 미리보기가 없어 null. */
export function libPreviewUrl(kind: string, url: string): string | null {
  if (!url) return null;
  if (kind === "report") return libViewUrl(url);
  if (kind === "excel") return libExcelViewUrl(url);
  return null;
}

/** 미리보기 패널이 지원하는 종류인가 (PDF·엑셀·CSV) */
export function libPreviewable(kind: string): boolean {
  return kind === "report" || kind === "excel";
}

export function libToast(msg: string) {
  const t = document.createElement("div");
  t.className = "lib-toast";
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 3200);
}

// 다운로드 — S3 는 크로스오리진이라 download 속성이 무시되므로 blob 로 받아 저장한다.
// CORS 로 fetch 가 막히면 새 탭 열기로 폴백(Content-Disposition 에 따라 저장됨).
export async function libDownload(rawUrl: string, fname: string) {
  const url = normalizeDocUrl(rawUrl);
  try {
    const r = await fetch(url);
    if (!r.ok) {
      libToast("링크가 만료됐습니다 — 원본 채팅에서 다시 생성해 주세요.");
      return;
    }
    const o = URL.createObjectURL(await r.blob());
    const dl = document.createElement("a");
    dl.href = o;
    dl.download = fname;
    document.body.appendChild(dl);
    dl.click();
    dl.remove();
    setTimeout(() => URL.revokeObjectURL(o), 4000);
  } catch {
    window.open(url, "_blank", "noopener");
  }
}
